import { Component, effect, inject, input, signal } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CreateFiscalYearFormType } from './create-fiscal-year.form.type';
import { DokaModalController, DokaModal, DokaNumberInput, DokaButton } from '@doka/angular-core';
import { I18NextEagerPipe } from 'angular-i18next';
import { InputDatePickerComponent } from 'src/app/shared/components/forms/input-date-picker.component';
import { CreateFiscalYearModel, PlansClient } from 'src/api/services/generated/api-clients';
import { firstValueFrom } from 'rxjs';

@Component({
  templateUrl: './create-fiscal-year-slide-over.component.html',
  imports: [DokaModal, I18NextEagerPipe, ReactiveFormsModule, DokaNumberInput, InputDatePickerComponent, DokaButton],
})
export class CreateFiscalYearSlideOverComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly plansClient = inject(PlansClient);

  public readonly divisionId = input<string | undefined>(undefined);
  public readonly latestYear = input.required<number>();

  protected readonly isLoading = signal(false);

  protected form: FormGroup<CreateFiscalYearFormType> = this.fb.group({
    year: this.fb.control(0, [Validators.required, Validators.min(1)]),
    latestPlanSubmissionDate: this.fb.control(new Date(), [Validators.required]),
  });

  protected latestYearHasChanged = effect(() => {
    const latestYear = this.latestYear();
    this.form.controls.year.setValue(latestYear + 1);
    this.form.controls.year.setValidators([Validators.required, Validators.min(latestYear + 1)]);
  });

  protected closeModal(result?: boolean, role?: string) {
    this.modalCtrl.dismiss(result, role);
  }

  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    try {
      this.isLoading.set(true);

      await firstValueFrom(
        this.plansClient.createFiscalYear(
          new CreateFiscalYearModel({
            year: this.form.value.year!,
            latestPlanSubmissionDate: this.form.value.latestPlanSubmissionDate!,
            countryId: null,
            divisionId: this.divisionId() ?? null,
            countryIds: null,
          }),
        ),
      );

      this.modalCtrl.dismiss(true, 'created');
    } catch (error) {
      console.error('Error creating fiscal year:', error);
    } finally {
      this.isLoading.set(false);
    }
  }
}
